import Axios from "axios";
import useAnalytics from "hooks/analytics";
import useBookingFlowRedesign from "hooks/useBookingFlowRedesign";
import { isScreeningFlow, ScreeningFlow } from "models/screening-flow";
import React, { useEffect, useState } from "react";
import { UserActionEvent } from "utils/analytics/events";
import {
    getIsEngageAppointment,
    getScreeningFlow,
} from "utils/session-storage";
import { QuestionnaireForm } from "../../../components";
import useAuthUser from "../../../hooks/auth-user";
import useLanguage from "../../../hooks/language";
import Questionnaire, { extractAnswers } from "../../../models/questionnaire";
import User, {
    expiredPendingQuestionnaire,
    hasPaidLastQuestionnaire,
    upcomingScreeningAppointmentForFlow,
} from "../../../models/user";
import ENDPOINTS from "../../../utils/endpoints";

interface MedicalEvaluationProps {
    onSubmit: (userData: Required<Pick<User, "lastUserQuestionnaire">>) => void;
    fromUserQuestionnaireId?: number | undefined;
}

const getPreviousQuestionnaireId = (
    authUser: User,
    screeningFlow: ScreeningFlow,
    fromUserQuestionnaireId?: number
): number | undefined => {
    // Legacy : User let a questionnaire expire and could have already paid for it
    const expiredQuestionnaire = expiredPendingQuestionnaire(authUser);
    if (
        hasPaidLastQuestionnaire({
            lastUserQuestionnaire: expiredQuestionnaire,
        } as User)
    ) {
        return expiredQuestionnaire.id;
    }

    // Legacy : User navigated backwards in the screening process and might have already paid
    if (fromUserQuestionnaireId) {
        return fromUserQuestionnaireId;
    }

    // User is editing their answers after booking appointment and would have already paid
    const upcomingAppointment = upcomingScreeningAppointmentForFlow(
        authUser,
        screeningFlow
    );
    if (upcomingAppointment) {
        return upcomingAppointment.userQuestionnaire.id;
    }

    return undefined;
};

const MedicalEvaluation: React.FunctionComponent<MedicalEvaluationProps> = ({
    onSubmit,
    fromUserQuestionnaireId,
}: MedicalEvaluationProps) => {
    const [authUser] = useAuthUser();
    const { translations } = useLanguage();
    const t = translations.questionnaireSection;
    const [analytics] = useAnalytics();
    const isBookingFlowRedesign = useBookingFlowRedesign();

    // States

    const [questionnaire, setQuestionnaire] = useState<null | Questionnaire>(
        null
    );
    const [isSaving, setSaving] = useState(false);

    // Effects

    useEffect(() => {
        fetchQuestionnaire();
    }, []);

    // Network

    const fetchQuestionnaire = async () => {
        const screeningFlow = getScreeningFlow();
        if (isScreeningFlow(screeningFlow)) {
            const { data } = await Axios.get(
                ENDPOINTS.QUESTIONNAIRE(screeningFlow)
            );
            setQuestionnaire(data);
        }
    };

    const submitQuestionnaire = async (questionnaire: Questionnaire) => {
        if (!authUser) return;

        setSaving(true);
        const answers = extractAnswers(questionnaire);
        const screeningFlow = getScreeningFlow();

        const originalQuestionnaireId = getPreviousQuestionnaireId(
            authUser,
            screeningFlow,
            fromUserQuestionnaireId
        );

        const body = {
            userId: authUser.id,
            questionnaireId: questionnaire.id,
            fromId: originalQuestionnaireId,
            isEngageAppointment: getIsEngageAppointment(),
            answers,
        };
        try {
            const { data } = await Axios.post(
                ENDPOINTS.USER_QUESTIONNAIRE,
                body
            );

            if (!isBookingFlowRedesign) {
                analytics
                    ?.cdp()
                    ?.trackEvent(
                        UserActionEvent.ScreeningMedicalEvaluationSubmitted
                    );
            }

            onSubmit({
                lastUserQuestionnaire: data,
            });
        } catch {
            setSaving(false);
        }
    };

    // Rendering

    return (
        <div>
            <h2 className="container">{t.header}</h2>
            {questionnaire && (
                <QuestionnaireForm
                    questionnaire={questionnaire}
                    onCompleteQuestionnaire={submitQuestionnaire}
                    loading={isSaving}
                />
            )}
        </div>
    );
};

export default MedicalEvaluation;
